import merge from 'lodash/merge';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { fetchCurrentUser, currentUserShowSuccess } from '../../ducks/user.duck';
import { getSavedAddresses, generateAddressId } from '../../util/dataExtractors';
import { getLocationOptions } from '../../util/maps';

// ================ Action types ================ //

export const SAVE_CONTACT_DETAILS_REQUEST = 'app/ContactDetailsPage/SAVE_CONTACT_DETAILS_REQUEST';
export const SAVE_CONTACT_DETAILS_SUCCESS = 'app/ContactDetailsPage/SAVE_CONTACT_DETAILS_SUCCESS';
export const SAVE_EMAIL_ERROR = 'app/ContactDetailsPage/SAVE_EMAIL_ERROR';
export const SAVE_PHONE_NUMBER_ERROR = 'app/ContactDetailsPage/SAVE_PHONE_NUMBER_ERROR';

export const SAVE_CONTACT_DETAILS_CLEAR = 'app/ContactDetailsPage/SAVE_CONTACT_DETAILS_CLEAR';


// Saved Addresses
export const SAVE_ADDRESS_REQUEST = 'app/ContactDetailsPage/SAVE_ADDRESS_REQUEST';
export const SAVE_ADDRESS_SUCCESS = 'app/ContactDetailsPage/SAVE_ADDRESS_SUCCESS';
export const SAVE_ADDRESS_ERROR = 'app/ContactDetailsPage/SAVE_ADDRESS_ERROR';
export const SAVE_ADDRESS_CLEAR = 'app/ContactDetailsPage/SAVE_ADDRESS_CLEAR';
export const DELETE_ADDRESS_REQUEST = 'app/ContactDetailsPage/DELETE_ADDRESS_REQUEST';
export const DELETE_ADDRESS_SUCCESS = 'app/ContactDetailsPage/DELETE_ADDRESS_SUCCESS';
export const DELETE_ADDRESS_ERROR = 'app/ContactDetailsPage/DELETE_ADDRESS_ERROR';
export const SET_DEFAULT_ADDRESS_REQUEST = 'app/ContactDetailsPage/SET_DEFAULT_ADDRESS_REQUEST';
export const SET_DEFAULT_ADDRESS_SUCCESS = 'app/ContactDetailsPage/SET_DEFAULT_ADDRESS_SUCCESS';
export const SET_DEFAULT_ADDRESS_ERROR = 'app/ContactDetailsPage/SET_DEFAULT_ADDRESS_ERROR';

// ================ Reducer ================ //

const initialState = {
  saveEmailError: null,
  savePhoneNumberError: null,
  saveContactDetailsInProgress: false,
  contactDetailsChanged: false,
  // Saved Addresses
  saveAddressInProgress: false,
  saveAddressError: null,
  deleteAddressInProgress: false,
  deleteAddressError: null,
  setDefaultAddressInProgress: false,
  setDefaultAddressError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SAVE_CONTACT_DETAILS_REQUEST:
      return {
        ...state,
        saveContactDetailsInProgress: true,
        saveEmailError: null,
        savePhoneNumberError: null,
        contactDetailsChanged: false,
      };
    case SAVE_CONTACT_DETAILS_SUCCESS:
      return { ...state, saveContactDetailsInProgress: false, contactDetailsChanged: true };
    case SAVE_EMAIL_ERROR:
      return { ...state, saveContactDetailsInProgress: false, saveEmailError: payload };
    case SAVE_PHONE_NUMBER_ERROR:
      return { ...state, saveContactDetailsInProgress: false, savePhoneNumberError: payload };

    case SAVE_CONTACT_DETAILS_CLEAR:
      return {
        ...state,
        saveContactDetailsInProgress: false,
        saveEmailError: null,
        savePhoneNumberError: null,
        contactDetailsChanged: false,
      };

    // Saved Addresses
    case SAVE_ADDRESS_REQUEST:
      return { ...state, saveAddressInProgress: true, saveAddressError: null };
    case SAVE_ADDRESS_SUCCESS:
      return { ...state, saveAddressInProgress: false, saveAddressError: null };
    case SAVE_ADDRESS_ERROR:
      return { ...state, saveAddressInProgress: false, saveAddressError: payload };
    case SAVE_ADDRESS_CLEAR:
      return { ...state, saveAddressError: null };

    case DELETE_ADDRESS_REQUEST:
      return { ...state, deleteAddressInProgress: true, deleteAddressError: null };
    case DELETE_ADDRESS_SUCCESS:
      return { ...state, deleteAddressInProgress: false, deleteAddressError: null };
    case DELETE_ADDRESS_ERROR:
      return { ...state, deleteAddressInProgress: false, deleteAddressError: payload };

    case SET_DEFAULT_ADDRESS_REQUEST:
      return { ...state, setDefaultAddressInProgress: true, setDefaultAddressError: null };
    case SET_DEFAULT_ADDRESS_SUCCESS:
      return { ...state, setDefaultAddressInProgress: false, setDefaultAddressError: null };
    case SET_DEFAULT_ADDRESS_ERROR:
      return { ...state, setDefaultAddressInProgress: false, setDefaultAddressError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const saveContactDetailsRequest = () => ({ type: SAVE_CONTACT_DETAILS_REQUEST });
export const saveContactDetailsSuccess = () => ({ type: SAVE_CONTACT_DETAILS_SUCCESS });
export const saveEmailError = error => ({
  type: SAVE_EMAIL_ERROR,
  payload: error,
  error: true,
});
export const savePhoneNumberError = error => ({
  type: SAVE_PHONE_NUMBER_ERROR,
  payload: error,
  error: true,
});

export const saveContactDetailsClear = () => ({ type: SAVE_CONTACT_DETAILS_CLEAR });

// Saved Addresses Action Creators
export const saveAddressRequest = () => ({ type: SAVE_ADDRESS_REQUEST });
export const saveAddressSuccess = () => ({ type: SAVE_ADDRESS_SUCCESS });
export const saveAddressError = error => ({ type: SAVE_ADDRESS_ERROR, payload: error, error: true });
// The address window opens without the last save's error
export const clearSaveAddressError = () => ({ type: SAVE_ADDRESS_CLEAR });

export const deleteAddressRequest = () => ({ type: DELETE_ADDRESS_REQUEST });
export const deleteAddressSuccess = () => ({ type: DELETE_ADDRESS_SUCCESS });
export const deleteAddressError = error => ({ type: DELETE_ADDRESS_ERROR, payload: error, error: true });

export const setDefaultAddressRequest = () => ({ type: SET_DEFAULT_ADDRESS_REQUEST });
export const setDefaultAddressSuccess = () => ({ type: SET_DEFAULT_ADDRESS_SUCCESS });
export const setDefaultAddressError = error => ({ type: SET_DEFAULT_ADDRESS_ERROR, payload: error, error: true });

// ================ Thunks ================ //

/**
 * Make a phone number update request to the API and return the current user.
 */
const requestSavePhoneNumber = params => (dispatch, getState, sdk) => {
  const phoneNumber = params.phoneNumber;

  return sdk.currentUser
    .updateProfile(
      { protectedData: { phoneNumber } },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }

      const currentUser = entities[0];
      return currentUser;
    })
    .catch(e => {
      dispatch(savePhoneNumberError(storableError(e)));
      // pass the same error so that the SAVE_CONTACT_DETAILS_SUCCESS
      // action will not be fired
      throw e;
    });
};

/**
 * Make a email update request to the API and return the current user.
 */
const requestSaveEmail = params => (dispatch, getState, sdk) => {
  const { email, currentPassword } = params;

  return sdk.currentUser
    .changeEmail(
      { email, currentPassword },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.changeEmail response');
      }

      const currentUser = entities[0];
      return currentUser;
    })
    .catch(e => {
      dispatch(saveEmailError(storableError(e)));
      // pass the same error so that the SAVE_CONTACT_DETAILS_SUCCESS
      // action will not be fired
      throw e;
    });
};

/**
 * Save email and update the current user.
 */
const saveEmail = params => (dispatch, getState, sdk) => {
  return (
    dispatch(requestSaveEmail(params))
      .then(user => {
        dispatch(currentUserShowSuccess(user));
        dispatch(saveContactDetailsSuccess());
      })
      // error action dispatched in requestSaveEmail
      .catch(e => null)
  );
};

/**
 * Save phone number and update the current user.
 */
const savePhoneNumber = params => (dispatch, getState, sdk) => {
  return (
    dispatch(requestSavePhoneNumber(params))
      .then(user => {
        dispatch(currentUserShowSuccess(user));
        dispatch(saveContactDetailsSuccess());
      })
      // error action dispatched in requestSavePhoneNumber
      .catch(e => null)
  );
};

/**
 * Save email and phone number and update the current user.
 */
const saveEmailAndPhoneNumber = params => (dispatch, getState, sdk) => {
  const { email, phoneNumber, currentPassword } = params;

  // order of promises: 1. email, 2. phone number
  const promises = [
    dispatch(requestSaveEmail({ email, currentPassword })),
    dispatch(requestSavePhoneNumber({ phoneNumber })),
  ];

  return Promise.all(promises)
    .then(values => {
      // Array of two user objects is resolved
      // the first one is from the email update
      // the second one is from the phone number update

      const saveEmailUser = values[0];
      const savePhoneNumberUser = values[1];

      // merge the protected data from the user object returned
      // by the phone update operation
      const protectedData = savePhoneNumberUser.attributes.profile.protectedData;
      const phoneNumberMergeSource = { attributes: { profile: { protectedData } } };

      const currentUser = merge(saveEmailUser, phoneNumberMergeSource);
      dispatch(currentUserShowSuccess(currentUser));
      dispatch(saveContactDetailsSuccess());
    })
    .catch(e => null);
};

/**
 * Update contact details, actions depend on which data has changed
 */
export const saveContactDetails = params => async (dispatch, getState, sdk) => {
  dispatch(saveContactDetailsRequest());

  const { email, currentEmail, phoneNumber, currentPhoneNumber, currentPassword, ...rest } = params;
  const emailChanged = email !== currentEmail;
  const phoneNumberChanged = phoneNumber !== currentPhoneNumber;

  // Check if we have address data to save
  const hasAddressData = rest.line1; // Use consistent field names

  if (hasAddressData) {
    // Save address to user protectedData in the correct format
    const shippingDetails = {
      city: rest.city,
      country: rest.country,
      line1: rest.line1,
      line2: rest.line2 || '',
      phone_number: rest.phone || '', // Save complete phone number (with country code if provided)
      postal_code: rest.postal_code,
      state: rest.state || '',
    };

    // Update protectedData with address and phone
    const updatedProtectedData = {
      phoneNumber: phoneNumber,
      shippingDetails: shippingDetails,
    };

    try {
      const response = await sdk.currentUser.updateProfile(
        { protectedData: updatedProtectedData },
        {
          expand: true,
          include: ['profileImage'],
          'fields.image': ['variants.square-small', 'variants.square-small2x'],
        }
      );

      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }

      const currentUser = entities[0];
      dispatch(currentUserShowSuccess(currentUser));
      // Note: EasyPost address is created on-demand when needed (checkout, shipping labels)
    } catch (e) {
      dispatch(savePhoneNumberError(storableError(e)));
      throw e;
    }
  }

  // Handle email changes separately if needed
  if (emailChanged && phoneNumberChanged && !hasAddressData) {
    return dispatch(saveEmailAndPhoneNumber({ email, currentPassword, phoneNumber }));
  } else if (emailChanged && !hasAddressData) {
    dispatch(saveEmail({ email, currentPassword }));
  } else if (phoneNumberChanged && !hasAddressData) {
    dispatch(savePhoneNumber({ phoneNumber }));
  }

  // If we saved address data above, we're done
  if (hasAddressData) {
    // Still handle email change if needed
    if (emailChanged) {
      await dispatch(saveEmail({ email, currentPassword }));
    }
    dispatch(saveContactDetailsSuccess());
  }
};

export const loadData = () => {
  // Since verify email happens in separate tab, current user's data might be updated
  return fetchCurrentUser();
};

// ================ Saved Addresses Thunks ================ //

/**
 * The profile's public location, from its default address: city, state and country, the
 * display options for them, and the display choice, kept while it is still one of them.
 * Without a default address all three are cleared. Options that couldn't be worked out
 * (geocoding failed) leave the saved ones as they are.
 */
const publicLocationFrom = async (currentUser, defaultAddress) => {
  if (!defaultAddress) {
    return { location: null, locationOptions: null, displayLocation: null };
  }
  const { city, state, country } = defaultAddress;
  const locationOptions = await getLocationOptions(city, state, country);
  let displayLocation = currentUser?.attributes?.profile?.publicData?.displayLocation || null;
  if (displayLocation && locationOptions) {
    const stillValid = locationOptions.some(opt => opt.label === displayLocation);
    if (!stillValid) {
      displayLocation = locationOptions[0]?.label || null;
    }
  }
  return {
    location: { city, state, country },
    ...(locationOptions ? { locationOptions } : {}),
    displayLocation,
  };
};

/**
 * Save a new address or update an existing one.
 * @param {Object} addressData - The address data to save
 * @param {string|null} addressId - If provided, updates existing address; otherwise creates new
 */
export const saveAddress = (addressData, addressId = null) => async (dispatch, getState, sdk) => {
  dispatch(saveAddressRequest());

  try {
    const state = getState();
    const currentUser = state.user.currentUser;
    const existingAddresses = getSavedAddresses(currentUser) || [];

    let updatedAddresses;

    let newAddressId = addressId; // Keep track of the address ID
    let isNewDefault = false;

    if (addressId) {
      // Update existing address
      updatedAddresses = existingAddresses.map(addr => {
        if (addr.id === addressId) {
          return {
            ...addr,
            ...addressData,
            id: addressId, // Keep the same ID
          };
        }
        return addr;
      });
      // Check if this is the default address being updated
      const updatedAddr = updatedAddresses.find(a => a.id === addressId);
      isNewDefault = updatedAddr?.isDefault;
    } else {
      // Create new address
      newAddressId = generateAddressId();
      isNewDefault = existingAddresses.length === 0; // First address is default
      const newAddress = {
        ...addressData,
        id: newAddressId,
        isDefault: isNewDefault,
        createdAt: new Date().toISOString(),
      };
      updatedAddresses = [...existingAddresses, newAddress];
    }

    // The default address gives the profile its public location
    const defaultAddress = updatedAddresses.find(a => a.isDefault) || updatedAddresses[0];
    const publicData = await publicLocationFrom(currentUser, defaultAddress);

    const response = await sdk.currentUser.updateProfile(
      {
        protectedData: { savedAddresses: updatedAddresses },
        publicData,
      },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    );

    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
    }

    dispatch(currentUserShowSuccess(entities[0]));
    dispatch(saveAddressSuccess());
    return { success: true, addressId: newAddressId };
  } catch (e) {
    dispatch(saveAddressError(storableError(e)));
    return { success: false, error: e };
  }
};

/**
 * Delete an address by ID.
 * @param {string} addressId - The ID of the address to delete
 */
export const deleteAddress = addressId => async (dispatch, getState, sdk) => {
  dispatch(deleteAddressRequest());

  try {
    const state = getState();
    const currentUser = state.user.currentUser;
    const existingAddresses = getSavedAddresses(currentUser) || [];

    const addressToDelete = existingAddresses.find(addr => addr.id === addressId);
    const remaining = existingAddresses.filter(addr => addr.id !== addressId);

    // If the default is deleted, the first one left becomes the default (as a copy: the
    // addresses in the store stay as they are until the save succeeds)
    const updatedAddresses =
      addressToDelete?.isDefault && remaining.length > 0
        ? remaining.map((addr, i) => (i === 0 ? { ...addr, isDefault: true } : addr))
        : remaining;

    const newDefaultAddress = updatedAddresses.find(a => a.isDefault) || updatedAddresses[0];
    const publicData = await publicLocationFrom(currentUser, newDefaultAddress);

    const response = await sdk.currentUser.updateProfile(
      {
        protectedData: { savedAddresses: updatedAddresses },
        publicData,
      },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    );

    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
    }

    dispatch(currentUserShowSuccess(entities[0]));
    dispatch(deleteAddressSuccess());
    return { success: true };
  } catch (e) {
    dispatch(deleteAddressError(storableError(e)));
    return { success: false, error: e };
  }
};

/**
 * Set an address as the default.
 * @param {string} addressId - The ID of the address to set as default
 */
export const setDefaultAddress = addressId => async (dispatch, getState, sdk) => {
  dispatch(setDefaultAddressRequest());

  try {
    const state = getState();
    const currentUser = state.user.currentUser;
    const existingAddresses = getSavedAddresses(currentUser) || [];

    // Update isDefault for all addresses
    const updatedAddresses = existingAddresses.map(addr => ({
      ...addr,
      isDefault: addr.id === addressId,
    }));

    const newDefaultAddress = updatedAddresses.find(a => a.id === addressId);
    const publicData = await publicLocationFrom(currentUser, newDefaultAddress);

    const response = await sdk.currentUser.updateProfile(
      {
        protectedData: { savedAddresses: updatedAddresses },
        publicData,
      },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    );

    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
    }

    dispatch(currentUserShowSuccess(entities[0]));
    dispatch(setDefaultAddressSuccess());
    return { success: true };
  } catch (e) {
    dispatch(setDefaultAddressError(storableError(e)));
    return { success: false, error: e };
  }
};
